import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { globalSearch, SearchResponse, SearchResult } from '../api/search';
import { SearchInput } from './SearchInput';

export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchCache, setSearchCache] = useState<Map<string, SearchResponse>>(new Map());
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search with longer delay for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 3) { // Increased minimum length
        performSearch();
      } else {
        setResults(null);
        setShowResults(false);
      }
    }, 500); // Increased debounce time

    return () => clearTimeout(timer);
  }, [query]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { keycloak } = useKeycloak();

  const performSearch = async () => {
    if (!query.trim()) return;

    const trimmedQuery = query.trim().toLowerCase();
    
    // Check cache first
    if (searchCache.has(trimmedQuery)) {
      setResults(searchCache.get(trimmedQuery) || null);
      setShowResults(true);
      return;
    }

    setIsSearching(true);
    try {
      const data = await globalSearch(keycloak.token || '', trimmedQuery, 10, 0); // Reduced limit for faster response
      setResults(data);
      setShowResults(true);
      
      // Cache the result
      setSearchCache(prev => {
        const newCache = new Map(prev);
        newCache.set(trimmedQuery, data);
        // Keep only last 10 searches in cache
        if (newCache.size > 10) {
          const firstKey = newCache.keys().next().value;
          if (firstKey) {
            newCache.delete(firstKey);
          }
        }
        return newCache;
      });
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!results) return;

    const allResults = [
      ...(results.results.projects || []),
      ...(results.results.people || []),
      ...(results.results.assets || []),
      ...(results.results.warranties || []),
      ...(results.results.tasks || []),
      ...(results.results.documents || []),
      ...(results.results.locations || []),
      ...(results.results.manufacturers || [])
    ];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < allResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && allResults[selectedIndex]) {
          navigate(allResults[selectedIndex].url);
          setShowResults(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setShowResults(false);
    setQuery('');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'project': return '🏗️';
      case 'person': return '👤';
      case 'asset': return '💻';
      case 'task': return '📋';
      case 'warranty': return '📄';
      case 'document': return '📄';
      case 'location': return '📍';
      case 'manufacturer': return '🏭';
      default: return '📄';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'project': return 'Projects';
      case 'person': return 'People';
      case 'asset': return 'Assets';
      case 'task': return 'Tasks';
      case 'warranty': return 'Warranties';
      case 'document': return 'Documents';
      case 'location': return 'Locations';
      case 'manufacturer': return 'Manufacturers';
      default: return 'Other';
    }
  };

  const renderResults = () => {
    if (!results) return null;

    const allResults = [
      ...(results.results.projects || []).map(r => ({ ...r, type: 'project' as const })),
      ...(results.results.people || []).map(r => ({ ...r, type: 'person' as const })),
      ...(results.results.assets || []).map(r => ({ ...r, type: 'asset' as const })),
      ...(results.results.warranties || []).map(r => ({ ...r, type: 'warranty' as const })),
      ...(results.results.tasks || []).map(r => ({ ...r, type: 'task' as const })),
      ...(results.results.documents || []).map(r => ({ ...r, type: 'document' as const })),
      ...(results.results.locations || []).map(r => ({ ...r, type: 'location' as const })),
      ...(results.results.manufacturers || []).map(r => ({ ...r, type: 'manufacturer' as const }))
    ];

    if (allResults.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          No results found for "{query}"
        </div>
      );
    }

    return (
      <div className="max-h-96 overflow-y-auto">
        {allResults.map((result, index) => (
          <div
            key={`${result.type}-${result.id}`}
            className={`p-3 hover:bg-gray-50 cursor-pointer ${
              index === selectedIndex ? 'bg-blue-50 border-l-2 border-blue-500' : ''
            }`}
            onClick={() => handleResultClick(result)}
          >
            <div className="flex items-center space-x-3">
              <span className="text-lg">{getTypeIcon(result.type)}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {result.name}
                </div>
                {result.description && (
                  <div className="text-sm text-gray-500 truncate">
                    {result.description}
                  </div>
                )}
                {result.company && (
                  <div className="text-xs text-gray-400">
                    {result.company} • {result.role}
                  </div>
                )}
                {result.uploadedBy && (
                  <div className="text-xs text-gray-400">
                    Uploaded by {result.uploadedBy} • {result.uploadedAt}
                  </div>
                )}
                {result.size && (
                  <div className="text-xs text-gray-400">
                    {(result.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-400">
                {getTypeLabel(result.type)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={searchRef} className="relative flex-1 max-w-2xl">
      <div className="relative">
        <SearchInput
          ref={inputRef}
          placeholder="Search anything..."
          value={query}
          onChange={setQuery}
          onSearch={performSearch}
          onFocus={() => {
            if (results && query.trim().length >= 3) {
              setShowResults(true);
            }
          }}
          onKeyDown={handleKeyDown}
          className="w-full"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults(null);
              setShowResults(false);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        )}
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <FaSpinner className="w-4 h-4 text-gray-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {renderResults()}
        </div>
      )}
    </div>
  );
}; 